import { useCallback, useEffect, useRef, useState } from 'react';
import { recordRapidCaptureMetric } from './rapidCaptureMetrics';

export function useCameraStream(active: boolean) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const requestGenerationRef = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [ready, setReady] = useState(false);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>(
    'environment',
  );
  const [cameraCount, setCameraCount] = useState(0);

  const stop = useCallback(() => {
    requestGenerationRef.current += 1;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setReady(false);
  }, []);

  const start = useCallback(
    async (nextFacing = facingMode) => {
      stop();
      const requestGeneration = requestGenerationRef.current;
      if (!navigator.mediaDevices?.getUserMedia) {
        setError(
          'This browser cannot open the camera. Choose a photo instead.',
        );
        return;
      }
      setStarting(true);
      const startedAt = performance.now();
      setError(null);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: nextFacing },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });
        if (requestGeneration !== requestGenerationRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          await video.play();
        }
        if (requestGeneration !== requestGenerationRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        setReady(true);
        recordRapidCaptureMetric(
          'camera-first-frame',
          performance.now() - startedAt,
        );
        const devices = await navigator.mediaDevices
          .enumerateDevices()
          .catch(() => []);
        setCameraCount(
          devices.filter(({ kind }) => kind === 'videoinput').length,
        );
      } catch (cameraError) {
        const name =
          cameraError instanceof DOMException ? cameraError.name : '';
        setError(
          name === 'NotAllowedError'
            ? 'Camera access was denied. Allow camera access or choose a photo.'
            : 'The camera could not be opened. Choose a photo or try again.',
        );
      } finally {
        setStarting(false);
      }
    },
    [facingMode, stop],
  );

  useEffect(() => {
    if (active) void start();
    else stop();
    return stop;
  }, [active, start, stop]);

  useEffect(() => {
    if (!active) return undefined;
    const pauseHiddenCamera = () => {
      if (document.visibilityState !== 'hidden') return;
      stop();
      setError(
        'Camera paused while qkIndex was in the background. Resume when ready.',
      );
    };
    document.addEventListener('visibilitychange', pauseHiddenCamera);
    return () =>
      document.removeEventListener('visibilitychange', pauseHiddenCamera);
  }, [active, stop]);

  const switchCamera = useCallback(() => {
    const next = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(next);
  }, [facingMode]);

  return {
    videoRef,
    error,
    starting,
    ready,
    facingMode,
    cameraCount,
    start,
    stop,
    switchCamera,
  };
}
